import { Quaternion, Vector3 } from 'three';
import { FACE_MESH_CANONICAL_CM } from '../render/facemesh/canonicalMesh';
import { LM, lmX, lmY, type LandmarkBuffer } from '../tracking/landmarks';
import { unproject, type CameraIntrinsics } from './CameraIntrinsics';

/**
 * Landmarks that ride rigidly with the skull: nose bridge and nasion, nose
 * sides, brow ridge, inner and outer canthi, cheekbones, forehead. No eyelids
 * (blinks), no mouth or jaw (expressions), no ear-front points (silhouette,
 * one side hidden as soon as the head turns).
 */
export const ANCHOR_LANDMARKS: readonly number[] = [
  // nose bridge, nasion, forehead centre line
  6, 197, 195, 5, 4, 1, 168, 8, 9, 151, 10,
  // nose sides
  48, 278, 115, 344, 220, 440, 45, 275, 51, 281, 3, 248, 196, 419, 174, 399, 236, 456, 198, 420, 131, 360, 49, 279, 102, 331, 64, 294, 129, 358,
  // canthi
  133, 362, 33, 263,
  // brow ridge
  70, 300, 63, 293, 105, 334, 66, 296, 107, 336, 55, 285, 65, 295, 52, 282, 53, 283, 46, 276,
  // cheekbones and upper cheeks
  116, 345, 123, 352, 117, 346, 118, 347, 50, 280, 101, 330, 36, 266, 111, 340, 121, 350, 120, 349, 119, 348, 47, 277, 126, 355, 142, 371, 209, 429,
  // temples and forehead sides
  127, 356, 162, 389, 21, 251, 54, 284, 103, 332, 67, 297, 109, 338, 68, 298, 104, 333, 69, 299, 108, 337,
];

/** Ear-to-ear width of the canonical MediaPipe face (landmarks 234–454), mm. */
export const CANONICAL_HEAD_WIDTH_MM = 153.2;
/** Points this far behind the canthi plane (after rotation) are on the head's silhouette or hidden: left out. */
const MAX_BEHIND_MM = 45;

export interface AnchorFitResult {
  /** Landmarks that contributed. */
  used: number;
  /** Median in-plane scatter of the single-point estimates around the result (mm) — a quality signal. */
  spreadMm: number;
}

/**
 * Fits the position of the inner-canthi midpoint over ~120 rigid landmarks
 * instead of reading it off two.
 *
 * With the head rotation known (MediaPipe's matrix, fitted over the whole
 * mesh) and the metric depth known (iris), every landmark i has a predicted
 * offset D_i = R · s · (P_i − P_canthi) from the canthi in camera space, where
 * P are the canonical model's points and s the user's size ratio. Its own
 * depth is therefore canthiDepth − D_i.z, its pixel back-projects to a camera
 * point X_i, and X_i − D_i is one estimate of the canthi position. Averaging
 * those estimates (after dropping the points that are hidden or far off the
 * consensus) divides the landmark noise by ~10 and no longer lets a squint, a
 * blink or a turn — which move the two canthi landmarks by pixels — move the
 * frame on the face.
 */
export class AnchorFit {
  private readonly indices: readonly number[];
  /** Canonical offsets from the canthi midpoint, mm, unscaled: [x, y, z] per landmark. */
  private readonly offsets: Float32Array;
  private readonly estimates: Float32Array;
  private readonly d = new Vector3();
  private readonly x = new Vector3();

  constructor(indices: readonly number[] = ANCHOR_LANDMARKS) {
    this.indices = indices;
    const cx = (canon(LM.RIGHT_EYE_INNER, 0) + canon(LM.LEFT_EYE_INNER, 0)) / 2;
    const cy = (canon(LM.RIGHT_EYE_INNER, 1) + canon(LM.LEFT_EYE_INNER, 1)) / 2;
    const cz = (canon(LM.RIGHT_EYE_INNER, 2) + canon(LM.LEFT_EYE_INNER, 2)) / 2;
    this.offsets = new Float32Array(indices.length * 3);
    indices.forEach((li, j) => {
      this.offsets[j * 3] = canon(li, 0) - cx;
      this.offsets[j * 3 + 1] = canon(li, 1) - cy;
      this.offsets[j * 3 + 2] = canon(li, 2) - cz;
    });
    this.estimates = new Float32Array(indices.length * 3);
  }

  /**
   * @param buf landmarks (normalised)
   * @param k camera intrinsics (pixels)
   * @param headQ head rotation (camera space)
   * @param canthiDepthMm metric depth of the canthi midpoint
   * @param scale user size / canonical size (head width ratio), 1 when unknown
   * @param out receives the canthi midpoint in camera space (mm)
   */
  solve(buf: LandmarkBuffer, k: CameraIntrinsics, headQ: Quaternion, canthiDepthMm: number, scale: number, out: Vector3): AnchorFitResult {
    const n = this.indices.length;
    const est = this.estimates;
    let used = 0;
    let sx = 0;
    let sy = 0;
    let sz = 0;
    for (let j = 0; j < n; j++) {
      this.d.set(this.offsets[j * 3]! * scale, this.offsets[j * 3 + 1]! * scale, this.offsets[j * 3 + 2]! * scale).applyQuaternion(headQ);
      if (this.d.z < -MAX_BEHIND_MM) continue;
      const depth = canthiDepthMm - this.d.z;
      if (depth < 50) continue;
      const li = this.indices[j]!;
      unproject(k, lmX(buf, li) * k.width, lmY(buf, li) * k.height, depth, this.x).sub(this.d);
      est[used * 3] = this.x.x;
      est[used * 3 + 1] = this.x.y;
      est[used * 3 + 2] = this.x.z;
      sx += this.x.x;
      sy += this.x.y;
      sz += this.x.z;
      used++;
    }
    if (used === 0) {
      // Degenerate (should not happen: the nose bridge always faces the camera): plain two-point anchor.
      const u = ((lmX(buf, LM.RIGHT_EYE_INNER) + lmX(buf, LM.LEFT_EYE_INNER)) / 2) * k.width;
      const v = ((lmY(buf, LM.RIGHT_EYE_INNER) + lmY(buf, LM.LEFT_EYE_INNER)) / 2) * k.height;
      unproject(k, u, v, canthiDepthMm, out);
      return { used: 0, spreadMm: 0 };
    }
    let mx = sx / used;
    let my = sy / used;
    let mz = sz / used;
    // Robust step: drop the estimates far off the consensus (a landmark that
    // slid on a shadow, an eyebrow raised), then average the rest.
    const r = new Float32Array(used);
    for (let i = 0; i < used; i++) r[i] = Math.hypot(est[i * 3]! - mx, est[i * 3 + 1]! - my);
    const sorted = Float32Array.from(r).sort();
    const medianR = sorted[used >> 1]!;
    const limit = Math.max(2 * medianR, 1.5);
    let kept = 0;
    sx = 0;
    sy = 0;
    sz = 0;
    for (let i = 0; i < used; i++) {
      if (r[i]! > limit) continue;
      sx += est[i * 3]!;
      sy += est[i * 3 + 1]!;
      sz += est[i * 3 + 2]!;
      kept++;
    }
    if (kept > 0) {
      mx = sx / kept;
      my = sy / kept;
      mz = sz / kept;
    }
    out.set(mx, my, mz);
    return { used: kept > 0 ? kept : used, spreadMm: medianR };
  }
}

function canon(index: number, axis: number): number {
  return FACE_MESH_CANONICAL_CM[index * 3 + axis]! * 10;
}
